import { useEffect, useState } from "react";
import { LineChart as LineIcon } from "lucide-react";
import api from "@/lib/api";
import { money, groupByWeek, groupByMonth } from "@/lib/analytics";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

function merge(incomeArr, costArr) {
  const map = {};
  incomeArr.forEach((i) => { map[i.label] = map[i.label] || { label: i.label, income: 0, cost: 0 }; map[i.label].income = i.value; });
  costArr.forEach((c) => { map[c.label] = map[c.label] || { label: c.label, income: 0, cost: 0 }; map[c.label].cost = c.value; });
  return Object.values(map).map((d) => ({ ...d, profit: d.income - d.cost }));
}

export default function ProfitLoss() {
  const [income, setIncome] = useState([]);
  const [costs, setCosts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [inc, cst] = await Promise.all([api.get("/income"), api.get("/analytics/costs")]);
        setIncome(inc.data); setCosts(cst.data.orders || []);
      } catch { /* */ }
    })();
  }, []);

  const totalIncome = income.reduce((s, i) => s + Number(i.amount || 0), 0);
  const totalCost = costs.reduce((s, c) => s + Number(c.cost || 0), 0);
  const net = totalIncome - totalCost;

  const weekly = merge(groupByWeek(income, "date", "amount"), groupByWeek(costs, "date", "cost"));
  const monthly = merge(groupByMonth(income, "date", "amount"), groupByMonth(costs, "date", "cost"));

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold tracking-tight">Profit &amp; Loss</h1>
      <p className="mt-1 text-sm text-muted-foreground">Income (insurance + rent) compared against order costs.</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <Stat label="Total Income" value={money(totalIncome)} color="text-primary" />
        <Stat label="Total Cost" value={money(totalCost)} color="text-orange-600" />
        <Stat label="Net Profit / Loss" value={money(net)} color={net >= 0 ? "text-primary" : "text-destructive"} />
      </div>

      <div className="mt-6 rounded-xl border border-border/60 bg-card p-5">
        <div className="mb-4 flex items-center gap-2"><LineIcon className="h-4 w-4 text-primary" /><h3 className="font-heading font-bold">Profit &amp; Loss Trend</h3></div>
        <Tabs defaultValue="weekly">
          <TabsList className="mb-4"><TabsTrigger value="weekly" data-testid="pl-tab-weekly">Weekly</TabsTrigger><TabsTrigger value="monthly" data-testid="pl-tab-monthly">Monthly</TabsTrigger></TabsList>
          <TabsContent value="weekly"><Chart data={weekly} /></TabsContent>
          <TabsContent value="monthly"><Chart data={monthly} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card p-5">
      <p className={`font-heading text-2xl font-extrabold ${color}`}>{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

function Chart({ data }) {
  return (
    <ResponsiveContainer width="100%" height={320}>
      <ComposedChart data={data}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="label" /><YAxis /><Tooltip formatter={(v) => money(v)} /><Legend />
        <Bar dataKey="income" name="Income" fill="hsl(175 84% 32%)" radius={[6, 6, 0, 0]} />
        <Bar dataKey="cost" name="Cost" fill="hsl(24 90% 55%)" radius={[6, 6, 0, 0]} />
        <Line dataKey="profit" name="Profit/Loss" stroke="hsl(217 80% 45%)" strokeWidth={3} dot={{ r: 4 }} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
